import {createStyles, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import {AgentFactorySummary} from 'llm/chat-library/factories/useAgentFactories';

const useListStyles = createStyles(() => ({
  list: {
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
  },
}));

const useCardStyles = createStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.white,
    width: '100%',
    textAlign: 'left',
    ':hover': {
      borderColor: theme.colors.blue[3],
      backgroundColor: theme.colors.gray[0],
    },
  },
}));

function AgentFactoryCard({
  agentFactory,
  onSelect,
}: {
  agentFactory: AgentFactorySummary;
  onSelect: (agentFactoryId: string) => void;
}): JSX.Element {
  const {classes} = useCardStyles();

  return (
    <UnstyledButton
      component="div"
      role="button"
      tabIndex={0}
      className={classes.card}
      aria-label={`Open ${agentFactory.name}`}
      onClick={() => {
        onSelect(agentFactory.id);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(agentFactory.id);
        }
      }}
    >
      <Text fw="bold" size="sm" truncate={true}>
        {agentFactory.name}
      </Text>
      {agentFactory.creatorName && (
        <Text size="xs" c="dimmed">
          by {agentFactory.creatorName}
        </Text>
      )}
      <Text size="xs" c="dimmed" lineClamp={2}>
        {agentFactory.description}
      </Text>
    </UnstyledButton>
  );
}

export default function AgentFactoriesList({
  agentFactories,
  onSelect,
}: {
  agentFactories: Array<AgentFactorySummary>;
  onSelect: (agentFactoryId: string) => void;
}): JSX.Element {
  const {classes} = useListStyles();

  if (agentFactories.length === 0) {
    return (
      <Text fw="normal" c="dimmed" size="sm">
        No factories yet.
      </Text>
    );
  }

  return (
    <Stack spacing="md" className={classes.list}>
      {agentFactories.map((agentFactory) => (
        <AgentFactoryCard key={agentFactory.id} agentFactory={agentFactory} onSelect={onSelect} />
      ))}
    </Stack>
  );
}
